/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { lazy, useEffect, Suspense, useContext } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RouteId } from './router/routes';
import MainScreenPict from './screens/CommonScreens/MainScreen/MainScreenPict';
import { actions } from 'redux-router5';
import { selectCurrentRouteName } from './data/store-selectors';
import { LoadingForSuspense } from './components/LoadingForSuspense/LoadingForSuspense';
import { getDefaultMonitoringRouteId } from './components/NavBar/navBarConfig';
import { AppAbilityContext } from './accessibility/ability';
import { RootState } from './data/store.typings';

const AuthScreen = lazy(() => import('./screens/CommonScreens/AuthScreen/AuthScreen'));
const SettingsWrapperScreen = lazy(() => import('./screens/RoutingScreens/SettingsWrapperScreen'));
const AdminWrapperScreen = lazy(() => import('./screens/RoutingScreens/AdminWrapperScreen'));
const MonitoringWrapperScreen = lazy(() => import('./screens/RoutingScreens/MonitoringWrapperScreen'));

export const AppRouter = () => {
    const dispatch = useDispatch();
    const routeName = useSelector(selectCurrentRouteName, shallowEqual);
    const authData = useSelector((state: RootState) => state.userReducer.authData, shallowEqual);
    const ability = useContext(AppAbilityContext);

    useEffect(() => {
        if (routeName.includes(RouteId.Login) && authData) {
            const routeId = getDefaultMonitoringRouteId(ability);
            if (routeId) dispatch(actions.navigateTo(routeId));
        }
        if (routeName !== RouteId.Main && !authData && !routeName.includes(RouteId.Login)) {
            dispatch(actions.navigateTo(RouteId.InspectorLogin));
        }
    });

    if (routeName === RouteId.Main) {
        return <MainScreenPict />;
    }

    if (process.env.REACT_APP_API_PREFIX && process.env.REACT_APP_API_PREFIX === 'pks-inspector') {
        if (routeName.includes(RouteId.Login)) {
            return (
                <Suspense fallback={<LoadingForSuspense />}>
                    <AuthScreen />
                </Suspense>
            );
        }

        if (authData) {
            if (routeName.includes(RouteId.SystemSettings)) {
                return (
                    <Suspense fallback={<LoadingForSuspense />}>
                        <SettingsWrapperScreen />
                    </Suspense>
                );
            }

            if (routeName.includes(RouteId.SystemAdmin)) {
                return (
                    <Suspense fallback={<LoadingForSuspense />}>
                        <AdminWrapperScreen />
                    </Suspense>
                );
            }

            if (routeName.includes(RouteId.Monitoring)) {
                return (
                    <Suspense fallback={<LoadingForSuspense />}>
                        <MonitoringWrapperScreen />
                    </Suspense>
                );
            }
        }
    }

    return <div>Page not found</div>;
};
